import React, { useState, useEffect, useMemo, useCallback } from "react";
import ReactDOM from "react-dom";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

// ─── Tokens ─────────────────────────────────────────────
const ink = "#0A0A0F";
const paper = "#F4F2EC";
const white = "#FFFFFF";
const accent = "#0033FF";
const muted = "rgba(10,10,15,0.58)";
const rule = "rgba(10,10,15,0.12)";
const danger = "#C8350A";
const dangerBg = "#FFE6DA";
const green = "#1B6B2A";
const amber = "#B96B05";
const fontBody = "'Archivo', system-ui, sans-serif";
const fontDisplay = "'Archivo Black', 'Archivo', sans-serif";
const fontMono = "'JetBrains Mono', monospace";

const STAGES = [
  { id: "research",  num: "01", name: "Research" },
  { id: "assigned",  num: "02", name: "Assigned" },
  { id: "files",     num: "03", name: "Files Received" },
  { id: "prototype", num: "04", name: "Prototyping" },
  { id: "adjust",    num: "05", name: "Needs Adjustment" },
  { id: "photo",     num: "06", name: "Photography" },
  { id: "listed",    num: "07", name: "Listed" },
];

// ─── Date helpers ───────────────────────────────────────
function todayDate() {
  const t = new Date();
  return new Date(t.getFullYear(), t.getMonth(), t.getDate());
}
function parseDate(str) {
  if (!str) return null;
  const [y, m, d] = String(str).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
function fmtDateInput(date) {
  if (!date) return "";
  const d = date instanceof Date ? date : parseDate(date);
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtDate(date) {
  if (!date) return "—";
  const d = date instanceof Date ? date : parseDate(date);
  if (!d) return "—";
  const M = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  return `${M[d.getMonth()]} ${d.getDate()}`;
}
function daysUntil(date) {
  const d = date instanceof Date ? date : parseDate(date);
  if (!d) return null;
  return Math.round((d.getTime() - todayDate().getTime()) / 86400000);
}
function deadlineState(date, stage) {
  if (stage === "listed") return "done";
  const days = daysUntil(date);
  if (days === null) return "none";
  if (days < 0) return "over";
  if (days <= 2) return "red";
  if (days <= 6) return "amber";
  return "green";
}
function deadlineLabel(date, stage) {
  if (stage === "listed") return "DONE";
  const days = daysUntil(date);
  if (days === null) return "—";
  if (days < 0) return `${Math.abs(days)}D OVER`;
  if (days === 0) return "DUE TODAY";
  if (days === 1) return "1 DAY";
  return `${days} DAYS`;
}
function deadlineColor(state) {
  if (state === "green") return green;
  if (state === "amber") return amber;
  if (state === "red") return danger;
  if (state === "over") return white;
  return muted;
}
function deadlineBg(state) {
  return state === "over" ? danger : "transparent";
}

function nowStamp() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
function initials(name) {
  if (!name) return "?";
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join("");
}
// Compact label for an assignee list: first name + "+N" suffix when there
// are more. Empty list returns the placeholder "UNASSIGNED".
function assigneeLabel(names) {
  if (!names || !names.length) return "UNASSIGNED";
  const first = names[0].split(" ")[0].toUpperCase();
  return names.length === 1 ? first : `${first} +${names.length - 1}`;
}
// ─── Icons (brutalist square caps, matches design) ───────
const I = {
  Plus: ({ s = 14 }) => (<svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/></svg>),
  Close: ({ s = 16 }) => (<svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M3 3l10 10M13 3 3 13" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/></svg>),
  Arrow: ({ s = 14 }) => (<svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/></svg>),
  Check: ({ s = 12 }) => (<svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M3 8.5 6.5 12 13 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/></svg>),
  Kanban: ({ s = 14 }) => (<svg width={s} height={s} viewBox="0 0 16 16" fill="none"><rect x="1.5" y="1.5" width="3.5" height="13" stroke="currentColor" strokeWidth="1.5"/><rect x="6.25" y="1.5" width="3.5" height="9" stroke="currentColor" strokeWidth="1.5"/><rect x="11" y="1.5" width="3.5" height="6" stroke="currentColor" strokeWidth="1.5"/></svg>),
  List: ({ s = 14 }) => (<svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M2 3.5h12M2 8h12M2 12.5h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/></svg>),
  Grip: ({ s = 14 }) => (<svg width={s} height={s} viewBox="0 0 16 16" fill="currentColor"><circle cx="6" cy="4" r="1"/><circle cx="10" cy="4" r="1"/><circle cx="6" cy="8" r="1"/><circle cx="10" cy="8" r="1"/><circle cx="6" cy="12" r="1"/><circle cx="10" cy="12" r="1"/></svg>),
  Calendar: ({ s = 12 }) => (<svg width={s} height={s} viewBox="0 0 16 16" fill="none"><rect x="1.5" y="3.5" width="13" height="11" stroke="currentColor" strokeWidth="1.5"/><path d="M1.5 6.5h13M5 1.5v3M11 1.5v3" stroke="currentColor" strokeWidth="1.5"/></svg>),
  Link: ({ s = 12 }) => (<svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M7 9 9 7M6.5 4 8 2.5a3 3 0 0 1 4 4L10.5 8M9.5 12 8 13.5a3 3 0 0 1-4-4L5.5 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/></svg>),
  Chat: ({ s = 12 }) => (<svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M2 3h12v8H8l-3 3v-3H2V3Z" stroke="currentColor" strokeWidth="1.5"/></svg>),
  Paperclip: ({ s = 12 }) => (<svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M11.5 6.5 6.7 11.3a2.5 2.5 0 1 1-3.5-3.5L8 3a1.7 1.7 0 0 1 2.4 2.4L5.6 10.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/></svg>),
  Download: ({ s = 13 }) => (<svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M8 2v8M4.5 7 8 10.5 11.5 7M3 13h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="square"/></svg>),
  AlertOutline: ({ s = 16 }) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 3 22 21H2L12 3Z" stroke="currentColor" strokeWidth="2"/><path d="M12 10v5" stroke="currentColor" strokeWidth="2" strokeLinecap="square"/><circle cx="12" cy="18" r="1" fill="currentColor"/></svg>),
  AlertFill: ({ s = 16 }) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 3 22 21H2L12 3Z" fill="currentColor"/><path d="M12 10v5" stroke="#fff" strokeWidth="2" strokeLinecap="square"/><circle cx="12" cy="18" r="1.2" fill="#fff"/></svg>),
};

// ─── Avatar ─────────────────────────────────────────────
function Avatar({ name, size = 24, title }) {
  const dim = { width: size, height: size };
  if (!name) {
    return (
      <span title={title || "Unassigned"} style={{
        ...dim, display: "inline-flex", alignItems: "center", justifyContent: "center",
        border: `1px dashed ${muted}`, color: muted, background: "transparent",
        fontFamily: fontDisplay, fontSize: Math.max(9, size * 0.4), flexShrink: 0,
      }}>?</span>
    );
  }
  return (
    <span title={title || name} style={{
      ...dim, display: "inline-flex", alignItems: "center", justifyContent: "center",
      background: ink, color: white, fontFamily: fontDisplay,
      fontSize: Math.max(9, size * 0.4), letterSpacing: "-0.02em",
      border: `1px solid ${ink}`, flexShrink: 0,
    }}>{initials(name)}</span>
  );
}

// Renders up to `max` overlapping avatars + a "+N" overflow chip. Empty list
// shows a single unassigned avatar so the row layout doesn't collapse.
function AvatarStack({ names = [], size = 24, max = 3 }) {
  const overlap = Math.round(size * 0.4);
  if (!names.length) {
    return <Avatar name={null} size={size}/>;
  }
  const shown = names.slice(0, max);
  const extra = names.length - shown.length;
  return (
    <span style={{ display: "inline-flex", alignItems: "center" }}>
      {shown.map((n, i) => (
        <span key={`${n}-${i}`} style={{ marginLeft: i === 0 ? 0 : -overlap, position: "relative", zIndex: shown.length - i }}>
          <Avatar name={n} size={size}/>
        </span>
      ))}
      {extra > 0 && (
        <span
          title={names.slice(max).join(", ")}
          style={{
            marginLeft: -overlap, position: "relative",
            width: size, height: size,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            background: white, color: ink, border: `1px solid ${ink}`,
            fontFamily: fontMono, fontSize: Math.max(9, size * 0.36), fontWeight: 700,
            flexShrink: 0,
          }}
        >+{extra}</span>
      )}
    </span>
  );
}

// Brutalist chip-based multi-select for picking assignees. Click any chip
// to toggle the employee in/out of the selection.
function AssigneePicker({ employees, selected = [], onChange }) {
  const isSel = (name) => selected.includes(name);
  const toggle = (name) => {
    if (isSel(name)) onChange(selected.filter((n) => n !== name));
    else onChange([...selected, name]);
  };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {employees.length === 0 && (
        <span style={{ fontFamily: fontMono, fontSize: 11, color: muted, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          NO EMPLOYEES
        </span>
      )}
      {employees.map((e) => {
        const sel = isSel(e.name);
        return (
          <button
            key={e.id || e.empId || e.name}
            type="button"
            onClick={() => toggle(e.name)}
            style={{
              fontFamily: fontMono, fontSize: 11, letterSpacing: "0.04em",
              padding: "5px 9px", cursor: "pointer",
              border: `1px solid ${ink}`,
              background: sel ? ink : white,
              color: sel ? white : ink,
              display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            {sel && <span style={{ fontSize: 10 }}>✓</span>}
            {e.name}
          </button>
        );
      })}
    </div>
  );
}

// ─── Deadline pill ──────────────────────────────────────
function Deadline({ date, stage }) {
  const state = deadlineState(date, stage);
  if (state === "none") return <span style={{ color: muted, fontFamily: fontMono, fontSize: 10, letterSpacing: "0.08em" }}>—</span>;
  return (
    <span style={{
      fontFamily: fontMono, fontSize: 10, letterSpacing: "0.08em",
      textTransform: "uppercase", padding: "2px 6px", fontWeight: 700,
      border: `1px solid ${state === "over" ? danger : deadlineColor(state)}`,
      background: deadlineBg(state),
      color: state === "over" ? white : deadlineColor(state),
      whiteSpace: "nowrap",
    }}>{deadlineLabel(date, stage)}</span>
  );
}

// ─── Card (kanban) ──────────────────────────────────────
function ProjectCard({ project, priorityRank, onOpen, dragHandlers, isDragging, compact }) {
  const alert = !!project.needsHelp;
  const cmt = project.comments?.length || 0;
  const files = (project.files?.length || 0) + (project.fileLinks?.length || 0);
  const [hover, setHover] = useState(false);
  const cardStyle = {
    background: white,
    border: `2px solid ${alert ? danger : ink}`,
    padding: compact ? "9px 10px" : 12,
    cursor: "pointer",
    display: "flex", flexDirection: "column",
    gap: compact ? 7 : 10,
    transition: "transform 0.12s ease, box-shadow 0.12s ease",
    opacity: isDragging ? 0.4 : 1,
    transform: hover ? "translate(-2px,-2px)" : "translate(0,0)",
    boxShadow: hover ? `4px 4px 0 ${accent}` : "none",
    userSelect: "none",
  };
  const isP1 = priorityRank <= 3;
  return (
    <div
      style={cardStyle}
      draggable
      {...dragHandlers}
      onClick={() => onOpen(project.id)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {project.imageUrl && !compact && (
        <div style={{ margin: "-12px -12px 0", borderBottom: `2px solid ${ink}`, height: 110, overflow: "hidden", background: "#fff" }}>
          <img src={project.imageUrl} alt="" loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{
            fontFamily: fontMono, fontSize: 10, letterSpacing: "0.1em",
            fontWeight: 700, padding: "2px 6px", flexShrink: 0,
            background: isP1 ? accent : "rgba(0,51,255,0.08)",
            color: isP1 ? white : accent,
          }}>P{priorityRank}</span>
          <span style={{ fontFamily: fontMono, fontSize: 10, color: muted, letterSpacing: "0.08em", textTransform: "uppercase" }}>{project.code}</span>
        </div>
        {alert && <span style={{ color: danger, flexShrink: 0 }} title="Needs help"><I.AlertFill s={18}/></span>}
      </div>
      <h3 style={{
        fontFamily: fontDisplay, fontSize: compact ? 14 : 17, lineHeight: 1.05,
        textTransform: "uppercase", letterSpacing: "-0.01em",
        margin: 0, wordBreak: "break-word", color: ink,
      }}>{project.title}</h3>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontFamily: fontMono, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: muted }}>
        {project.deadline && (
          <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <I.Calendar/> {fmtDate(project.deadline)}
          </span>
        )}
        {files > 0 && <span style={{ display: "flex", alignItems: "center", gap: 5 }}><I.Paperclip/> {files}</span>}
        {cmt > 0 && <span style={{ display: "flex", alignItems: "center", gap: 5 }}><I.Chat/> {cmt}</span>}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <AvatarStack names={project.assignees || []}/>
          <span style={{ fontFamily: fontMono, fontSize: 10, color: muted, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            {assigneeLabel(project.assignees)}
          </span>
        </div>
        <Deadline date={project.deadline} stage={project.stage}/>
      </div>
    </div>
  );
}

// ─── List row ───────────────────────────────────────────
function ProjectRow({ project, priorityRank, onOpen, gripHandlers, rowHandlers, isDragging, dropPos, compact }) {
  const alert = !!project.needsHelp;
  const stageIdx = STAGES.findIndex((s) => s.id === project.stage);
  const stage = STAGES[stageIdx];
  const [hover, setHover] = useState(false);
  const tdPad = compact ? "8px 12px" : "14px";
  // dropPos: "above" | "below" | null. Renders insertion line on the
  // matching edge of every <td> in the row so it stretches full-width.
  const topLine = dropPos === "above" ? `2px solid ${accent}` : "none";
  const bottomLine = dropPos === "below" ? `2px solid ${accent}` : `1px solid ${rule}`;
  return (
    <tr
      {...rowHandlers}
      onClick={() => onOpen(project.id)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover ? "rgba(0,51,255,0.04)" : "transparent",
        opacity: isDragging ? 0.4 : 1,
        cursor: "pointer",
      }}
    >
      <td style={{ width: 44, padding: tdPad, borderBottom: bottomLine, borderTop: topLine, verticalAlign: "middle" }}>
        <span
          {...gripHandlers}
          draggable
          onClick={(e) => e.stopPropagation()}
          style={{ cursor: "grab", color: muted, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 18 }}
        ><I.Grip/></span>
      </td>
      <td style={{ width: 56, padding: tdPad, borderBottom: bottomLine, borderTop: topLine, verticalAlign: "middle" }}>
        <span style={{
          fontFamily: fontMono, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
          padding: "3px 7px", display: "inline-block", minWidth: 28, textAlign: "center",
          background: priorityRank <= 3 ? accent : "rgba(0,51,255,0.08)",
          color: priorityRank <= 3 ? white : accent,
        }}>P{priorityRank}</span>
      </td>
      <td style={{ width: 28, padding: tdPad, borderBottom: bottomLine, borderTop: topLine, borderLeft: alert ? `4px solid ${danger}` : "none", verticalAlign: "middle" }}>
        {alert && <span style={{ color: danger }} title={project.needsHelp.note}><I.AlertFill s={18}/></span>}
      </td>
      <td style={{ padding: tdPad, borderBottom: bottomLine, borderTop: topLine, verticalAlign: "middle" }}>
        <div style={{ fontFamily: fontDisplay, fontSize: 15, textTransform: "uppercase", letterSpacing: "-0.01em", lineHeight: 1.1 }}>{project.title}</div>
        <div style={{ fontFamily: fontMono, fontSize: 10, color: muted, marginTop: 4, letterSpacing: "0.08em", textTransform: "uppercase" }}>{project.code}</div>
      </td>
      <td style={{ width: 170, padding: tdPad, borderBottom: bottomLine, borderTop: topLine, verticalAlign: "middle" }}>
        <span style={{
          fontFamily: fontMono, fontSize: 10, letterSpacing: "0.1em",
          textTransform: "uppercase", fontWeight: 500,
          padding: "4px 8px", border: `1px solid ${ink}`,
          display: "inline-block", whiteSpace: "nowrap",
        }}>{stage.num} · {stage.name}</span>
      </td>
      <td style={{ width: 150, padding: tdPad, borderBottom: bottomLine, borderTop: topLine, verticalAlign: "middle" }}>
        <div style={{ display: "flex", gap: 2, width: 130, height: 10 }} title={`Stage ${stageIdx + 1} of ${STAGES.length}`}>
          {STAGES.map((s, i) => {
            const isDone = i < stageIdx;
            const isCurrent = i === stageIdx;
            return (
              <div key={s.id} style={{
                flex: 1,
                background: isDone ? ink : isCurrent ? (alert ? danger : accent) : rule,
              }}/>
            );
          })}
        </div>
      </td>
      <td style={{ width: 170, padding: tdPad, borderBottom: bottomLine, borderTop: topLine, verticalAlign: "middle" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <AvatarStack names={project.assignees || []}/>
          <span style={{ fontFamily: fontMono, fontSize: 11, letterSpacing: "0.04em" }}>
            {(project.assignees && project.assignees.length)
              ? (project.assignees.length === 1
                  ? project.assignees[0]
                  : `${project.assignees[0].split(" ")[0]} +${project.assignees.length - 1}`)
              : <span style={{ color: muted, textTransform: "uppercase" }}>UNASSIGNED</span>}
          </span>
        </div>
      </td>
      <td style={{ width: 120, padding: tdPad, borderBottom: bottomLine, borderTop: topLine, verticalAlign: "middle" }}>
        <Deadline date={project.deadline} stage={project.stage}/>
      </td>
      <td style={{ width: 90, padding: tdPad, borderBottom: bottomLine, borderTop: topLine, textAlign: "right", verticalAlign: "middle" }}>
        {(project.comments?.length || 0) > 0 && (
          <span style={{ fontFamily: fontMono, color: muted, fontSize: 11, letterSpacing: "0.04em", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <I.Chat/> {project.comments.length}
          </span>
        )}
      </td>
    </tr>
  );
}

// ─── Reusable button ────────────────────────────────────
const btnSm = (variant = "default") => {
  const base = {
    fontFamily: fontBody, fontWeight: 700, fontSize: 11,
    letterSpacing: "0.06em", textTransform: "uppercase",
    padding: "7px 12px", border: `2px solid ${ink}`,
    background: "transparent", color: ink, cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 6,
  };
  if (variant === "accent") return { ...base, background: accent, color: white, borderColor: accent };
  if (variant === "danger") return { ...base, borderColor: danger, color: danger };
  return base;
};

// ─── Stage stepper ──────────────────────────────────────
function StageStepper({ stage, onChange }) {
  const currentIdx = STAGES.findIndex((s) => s.id === stage);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, border: `1px solid ${rule}`, background: white }}>
      {STAGES.map((s, i) => {
        const done = i < currentIdx;
        const current = i === currentIdx;
        return (
          <div
            key={s.id}
            onClick={() => onChange(s.id)}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 12px",
              fontFamily: fontMono, fontSize: 11, letterSpacing: "0.08em",
              textTransform: "uppercase", cursor: "pointer",
              borderLeft: `3px solid ${current ? accent : "transparent"}`,
              background: current ? "rgba(0,51,255,0.06)" : "transparent",
              color: done ? muted : ink,
              fontWeight: current ? 700 : 500,
            }}
          >
            <span style={{
              width: 22, height: 22, display: "inline-flex", alignItems: "center", justifyContent: "center",
              fontFamily: fontMono, fontSize: 10, fontWeight: 700,
              background: current ? accent : done ? ink : rule,
              color: (current || done) ? white : ink,
            }}>{s.num}</span>
            <span style={{ flex: 1 }}>{s.name}</span>
            {done && <span style={{ color: ink }}><I.Check s={14}/></span>}
          </div>
        );
      })}
    </div>
  );
}

// ─── Help block (active + form variants) ────────────────
function HelpBlock({ project, onClear }) {
  if (!project.needsHelp) return null;
  return (
    <div style={{ border: `2px solid ${danger}`, background: dangerBg, padding: 14, marginBottom: 16 }}>
      <div style={{
        fontFamily: fontDisplay, fontSize: 14, textTransform: "uppercase",
        letterSpacing: "-0.01em", color: danger,
        display: "flex", alignItems: "center", gap: 8, marginBottom: 8,
      }}>
        <I.AlertFill s={18}/> NEEDS HELP
      </div>
      <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: ink, marginBottom: 6 }}>
        FROM <strong>{(project.assignees && project.assignees.length) ? project.assignees.join(", ") : "—"}</strong>
        {project.needsHelp.helperName && <> · ASSIGNED TO <strong>{project.needsHelp.helperName}</strong></>}
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: ink }}>{project.needsHelp.note}</div>
      <div style={{ marginTop: 10 }}>
        <button style={btnSm()} onClick={onClear}>
          <I.Check/> RESOLVE
        </button>
      </div>
    </div>
  );
}

function HelpRequestForm({ employees, currentAssignees = [], onSubmit, onCancel }) {
  const [note, setNote] = useState("");
  const [helperName, setHelperName] = useState("");
  const helpers = employees.filter((e) => !currentAssignees.includes(e.name));
  const canSubmit = note.trim() && helperName;
  return (
    <div style={{ border: `2px solid ${danger}`, background: paper, padding: 14, marginBottom: 16 }}>
      <div style={{
        fontFamily: fontDisplay, fontSize: 14, textTransform: "uppercase",
        letterSpacing: "-0.01em", color: danger,
        display: "flex", alignItems: "center", gap: 8, marginBottom: 8,
      }}>
        <I.AlertOutline s={18}/> FLAG: NEEDS HELP
      </div>
      <FieldRow label="Who can help?">
        <select
          value={helperName}
          onChange={(e) => setHelperName(e.target.value)}
          style={fieldInputStyle}
        >
          <option value="">— select —</option>
          {helpers.map((e) => <option key={e.id || e.empId || e.name} value={e.name}>{e.name}{e.position ? ` · ${e.position}` : ""}</option>)}
        </select>
      </FieldRow>
      <div style={{ marginTop: 10 }}>
        <span style={{ ...fieldLabelStyle, display: "block", marginBottom: 6, width: "auto" }}>What's stuck? (required)</span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Be specific. What did you try, what's blocking, what do you need from them?"
          style={{
            width: "100%", resize: "vertical", minHeight: 60,
            fontFamily: fontBody, fontSize: 13, lineHeight: 1.5,
            background: white, border: `2px solid ${ink}`,
            padding: "10px 12px", outline: "none", color: ink,
          }}
        />
      </div>
      <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
        <button
          style={{ ...btnSm("danger"), opacity: canSubmit ? 1 : 0.4, cursor: canSubmit ? "pointer" : "not-allowed" }}
          disabled={!canSubmit}
          onClick={() => onSubmit({ note: note.trim(), helperName })}
        >
          <I.AlertFill s={12}/> FLAG IT
        </button>
        <button style={btnSm()} onClick={onCancel}>CANCEL</button>
      </div>
    </div>
  );
}

// ─── Field row / input styles ───────────────────────────
const fieldLabelStyle = {
  fontFamily: fontMono, fontSize: 10, letterSpacing: "0.12em",
  textTransform: "uppercase", color: muted, width: 88, flexShrink: 0,
};
const fieldInputStyle = {
  fontFamily: fontMono, fontSize: 12, letterSpacing: "0.04em",
  background: white, border: `1px solid ${ink}`,
  padding: "6px 8px", outline: "none", color: ink, width: "100%",
};
function FieldRow({ label, children, top = false }) {
  return (
    <div style={{
      display: "flex", alignItems: top ? "flex-start" : "center", gap: 10,
      padding: "10px 0", borderBottom: `1px dashed ${rule}`,
    }}>
      <span style={{ ...fieldLabelStyle, paddingTop: top ? 6 : 0 }}>{label}</span>
      <div style={{ flex: 1, fontSize: 13 }}>{children}</div>
    </div>
  );
}

// ─── Comments ───────────────────────────────────────────
function CommentList({ comments, onAdd }) {
  const [text, setText] = useState("");
  return (
    <>
      {comments.length === 0 && (
        <div style={{ fontFamily: fontMono, color: muted, padding: "8px 0", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}>NO COMMENTS YET.</div>
      )}
      {comments.map((c, i) => (
        <div key={i} style={{ display: "flex", gap: 10, padding: "12px 0", borderBottom: i < comments.length - 1 ? `1px solid ${rule}` : "none" }}>
          <Avatar name={c.author}/>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: muted, marginBottom: 4 }}>
              <strong style={{ color: ink, fontWeight: 700 }}>{c.author}</strong> · {c.at}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{c.text}</div>
          </div>
        </div>
      ))}
      <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a comment…"
          style={{
            flex: 1, resize: "vertical", minHeight: 60,
            fontFamily: fontBody, fontSize: 13, lineHeight: 1.5,
            background: white, border: `2px solid ${ink}`,
            padding: "10px 12px", outline: "none", color: ink,
          }}
        />
        <button
          style={{ ...btnSm("accent"), alignSelf: "flex-end", opacity: text.trim() ? 1 : 0.4, cursor: text.trim() ? "pointer" : "not-allowed" }}
          disabled={!text.trim()}
          onClick={() => { onAdd(text.trim()); setText(""); }}
        >
          POST <I.Arrow s={12}/>
        </button>
      </div>
    </>
  );
}

// ─── Detail drawer ──────────────────────────────────────
function DetailDrawer({ project, employees, actions, canDelete, onClose }) {
  const [showHelpForm, setShowHelpForm] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [, setTick] = useState(0);
  const stageName = STAGES.find((s) => s.id === project.stage)?.name || "—";

  // Live-tick the stopwatch once per second while it's running.
  const timerRunning = !!project.timerRunningSince;
  useEffect(() => {
    if (!timerRunning) return undefined;
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, [timerRunning]);
  // Pull the product image once if there's a source link but no image cached yet.
  useEffect(() => {
    if (project.sourceUrl && !project.imageUrl) actions.refreshImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);
  const liveSeconds = (project.designLoggedSeconds || 0) +
    (timerRunning ? Math.max(0, (Date.now() - new Date(project.timerRunningSince).getTime()) / 1000) : 0);
  const fmtDur = (sec) => {
    const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = Math.floor(sec % 60);
    return timerRunning ? `${h}h ${m}m ${s}s` : `${h}h ${m}m`;
  };

  const uploadFiles = (fileList) => {
    let files = Array.from(fileList || []);
    if (!files.length) return;
    if (files.length > 30) {
      window.alert("You can upload up to 30 files at a time. Only the first 30 will be uploaded.");
      files = files.slice(0, 30);
    }
    actions.uploadFile(files);
  };

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "rgba(10,10,15,0.4)",
        display: "flex", alignItems: "stretch", justifyContent: "flex-end",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(620px, 100vw)",
          background: paper,
          borderLeft: `2px solid ${ink}`,
          display: "flex", flexDirection: "column",
          overflow: "hidden",
          animation: "pdSlideIn 0.2s ease",
        }}
      >
        <style>{`@keyframes pdSlideIn { from { transform: translateX(40px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>

        {/* Header */}
        <div style={{
          padding: "18px 22px", borderBottom: `2px solid ${ink}`,
          display: "flex", alignItems: "flex-start", gap: 14, justifyContent: "space-between",
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11, letterSpacing: "0.12em",
              textTransform: "uppercase", color: accent, fontWeight: 500, marginBottom: 8,
            }}>
              {project.code} · {stageName}
            </div>
            <h2 style={{
              fontFamily: fontDisplay, fontSize: 32, textTransform: "uppercase",
              letterSpacing: "-0.02em", lineHeight: 0.95, margin: 0, wordBreak: "break-word",
            }}>{project.title}</h2>
            {phaseMetrics(project).length > 0 && (
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                {phaseMetrics(project).map((m) => (
                  <div key={m.label} title={`${m.sub}${m.done ? "" : " (in progress)"}`} style={{
                    fontFamily: fontMono, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase",
                    padding: "4px 9px",
                    border: `1px solid ${m.done ? "#34d399" : ink}`,
                    background: m.done ? "rgba(52,211,153,0.12)" : "transparent",
                    color: m.done ? "#0c3a2a" : muted,
                  }}>
                    {m.label}: {m.days}d{m.done ? "" : " · ongoing"}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent", border: `2px solid ${ink}`,
              width: 36, height: 36, cursor: "pointer", color: ink,
              display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}
          ><I.Close/></button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", display: "grid", gridTemplateColumns: "minmax(0, 1fr) 280px" }}>
          {/* Main */}
          <div style={{ padding: 22, borderRight: `1px solid ${rule}`, minWidth: 0 }}>
            {project.imageUrl && (
              <div style={{ marginBottom: 18 }}>
                <div style={{ border: `2px solid ${ink}`, background: "#fff", display: "flex", justifyContent: "center", overflow: "hidden" }}>
                  <img src={project.imageUrl} alt={project.title}
                    style={{ maxWidth: "100%", maxHeight: 300, objectFit: "contain", display: "block" }} />
                </div>
                {project.sourceUrl && (
                  <button onClick={() => actions.refreshImage()} style={{
                    fontFamily: fontMono, fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase",
                    color: muted, background: "transparent", border: "none", cursor: "pointer", padding: "6px 0 0",
                  }}>↻ Refresh image</button>
                )}
              </div>
            )}
            {project.needsHelp ? (
              <HelpBlock project={project} onClear={actions.clearHelp}/>
            ) : showHelpForm ? (
              <HelpRequestForm
                employees={employees}
                currentAssignees={project.assignees || []}
                onCancel={() => setShowHelpForm(false)}
                onSubmit={(d) => { actions.flagHelp(d); setShowHelpForm(false); }}
              />
            ) : null}

            <DetailSection label="DESIGN TIME (LOGGED)">
              <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                <span style={{ fontFamily: fontMono, fontSize: 24, fontWeight: 700, color: timerRunning ? accent : ink, letterSpacing: "0.02em" }}>
                  {fmtDur(liveSeconds)}
                </span>
                <button
                  onClick={() => actions.toggleTimer(timerRunning)}
                  style={{
                    fontFamily: fontMono, fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase",
                    padding: "9px 16px", cursor: "pointer", color: white,
                    border: `2px solid ${timerRunning ? danger : ink}`,
                    background: timerRunning ? danger : accent,
                    display: "inline-flex", alignItems: "center", gap: 6,
                  }}
                >
                  {timerRunning ? "■ STOP TIMER" : ((project.designSessions || 0) > 0 ? "▶ RESUME TIMER" : "▶ START TIMER")}
                </button>
                {timerRunning && (
                  <span style={{ fontFamily: fontMono, fontSize: 11, color: muted, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                    running{project.timerStartedBy ? ` · ${project.timerStartedBy}` : ""}
                  </span>
                )}
                {(project.designSessions || 0) > 0 && (
                  <span style={{
                    fontFamily: fontMono, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase",
                    color: ink, padding: "4px 9px", border: `1px solid ${ink}`,
                  }}>
                    {project.designSessions} session{project.designSessions === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              <div style={{ fontFamily: fontMono, fontSize: 10, color: muted, letterSpacing: "0.06em", textTransform: "uppercase", marginTop: 8 }}>
                Resume as you work — sessions add up across days, the total never resets.
              </div>
            </DetailSection>

            <DetailSection label="RESEARCH NOTES">
              <div style={{ fontSize: 14, lineHeight: 1.55, color: ink, whiteSpace: "pre-wrap" }}>{project.notes || "—"}</div>
            </DetailSection>

            <DetailSection label="SOURCE">
              {project.sourceUrl ? (
                <a href={project.sourceUrl} target="_blank" rel="noreferrer" style={detailLinkStyle}>{project.sourceUrl}</a>
              ) : (
                <div style={{ fontFamily: fontMono, fontSize: 11, color: muted, letterSpacing: "0.08em", textTransform: "uppercase" }}>—</div>
              )}
            </DetailSection>

            <DetailSection label="FILES">
              {(project.files || []).some((f) => f.driveId || f.driveLink) && (
                <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                  <button
                    onClick={() => actions.downloadAll()}
                    style={{ ...btnSm(), display: "inline-flex", alignItems: "center", gap: 6 }}
                  >
                    <I.Download s={13}/> DOWNLOAD ALL
                  </button>
                  {project.driveFolderLink && (
                    <a
                      href={project.driveFolderLink} target="_blank" rel="noreferrer"
                      style={{ ...btnSm(), display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}
                    >
                      <I.Link s={13}/> OPEN IN DRIVE
                    </a>
                  )}
                </div>
              )}
              {(project.files || []).map((f, i) => (
                <div key={`f${i}`} style={fileRowStyle}>
                  <span style={{ color: accent, flexShrink: 0 }}><I.Paperclip s={14}/></span>
                  {f.driveLink ? (
                    <a href={f.driveLink} target="_blank" rel="noreferrer" style={{ ...fileNameStyle, ...detailLinkStyle, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</a>
                  ) : (
                    <span style={fileNameStyle}>{f.name}</span>
                  )}
                  <span style={fileSizeStyle}>{f.size}</span>
                </div>
              ))}
              {(project.fileLinks || []).map((l, i) => (
                <div key={`l${i}`} style={fileRowStyle}>
                  <span style={{ color: accent, flexShrink: 0 }}><I.Link s={14}/></span>
                  <a href={l.url} target="_blank" rel="noreferrer" style={{
                    ...detailLinkStyle, flex: 1, minWidth: 0, overflow: "hidden",
                    textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{l.label}</a>
                </div>
              ))}
              {(project.files?.length || 0) === 0 && (project.fileLinks?.length || 0) === 0 && (
                <div style={{ fontFamily: fontMono, fontSize: 11, color: muted, letterSpacing: "0.08em", textTransform: "uppercase", padding: "4px 0" }}>NO FILES YET.</div>
              )}
              <div
                onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
                onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
                style={{
                  marginTop: 8, padding: "12px", textAlign: "center",
                  border: `2px dashed ${dragOver ? accent : "#0A0A0F"}`,
                  background: dragOver ? "rgba(0,51,255,0.06)" : "transparent",
                  transition: "all .15s",
                }}
              >
                <div style={{ fontFamily: fontMono, fontSize: 11, color: dragOver ? accent : muted, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
                  {dragOver ? "Drop to upload" : "Drag files here, or"}
                </div>
                <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                <label style={{ ...btnSm(), cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <I.Paperclip/> UPLOAD FILES
                  <input type="file" multiple style={{ display: "none" }} onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
                </label>
                <button style={btnSm()} onClick={() => actions.addLink()}>
                  <I.Link/> ADD LINK
                </button>
                </div>
              </div>
            </DetailSection>

            <DetailSection label={`DISCUSSION · ${project.comments?.length || 0}`}>
              <CommentList comments={project.comments || []} onAdd={actions.addComment}/>
            </DetailSection>
          </div>

          {/* Side */}
          <div style={{ padding: 22, background: "rgba(10,10,15,0.025)" }}>
            <DetailSection label="STAGE">
              <StageStepper stage={project.stage} onChange={actions.setStage}/>
            </DetailSection>

            <DetailSection label="META">
              <FieldRow label="Code">
                <span style={{ fontFamily: fontMono, fontSize: 12, letterSpacing: "0.04em" }}>{project.code}</span>
              </FieldRow>
              <FieldRow label="Assignees" top>
                <AssigneePicker
                  employees={employees}
                  selected={project.assignees || []}
                  onChange={actions.setAssignees}
                />
              </FieldRow>
              <FieldRow label="Deadline">
                <input
                  type="date"
                  value={fmtDateInput(project.deadline)}
                  onChange={(e) => actions.setDeadline(e.target.value || null)}
                  style={fieldInputStyle}
                />
              </FieldRow>
              <FieldRow label="Priority">
                <span style={{ fontFamily: fontMono, fontSize: 12, letterSpacing: "0.04em" }}>P{actions.priorityRank}</span>
              </FieldRow>
            </DetailSection>

            {!project.needsHelp && !showHelpForm && (
              <div style={{ marginBottom: 22 }}>
                <button
                  style={{ ...btnSm("danger"), width: "100%", justifyContent: "center" }}
                  onClick={() => setShowHelpForm(true)}
                >
                  <I.AlertOutline s={12}/> FLAG · NEEDS HELP
                </button>
              </div>
            )}

            {canDelete && (
              <div style={{ marginBottom: 22 }}>
                <button
                  style={{ ...btnSm("danger"), width: "100%", justifyContent: "center" }}
                  onClick={() => {
                    if (window.confirm(`Delete "${project.title}"? This can't be undone.`)) {
                      actions.deleteProject();
                    }
                  }}
                >
                  DELETE PROJECT
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

const detailLinkStyle = {
  fontFamily: fontMono, fontSize: 12, letterSpacing: "0.04em",
  color: accent, textDecoration: "underline", textUnderlineOffset: "3px",
  wordBreak: "break-all",
};
const fileRowStyle = {
  display: "flex", alignItems: "center", gap: 10,
  padding: "10px 12px", border: `1px solid ${rule}`, background: white,
  marginBottom: 6, minWidth: 0,
};
const fileNameStyle = {
  fontFamily: fontMono, fontSize: 12, letterSpacing: "0.02em",
  fontWeight: 500, flex: 1, minWidth: 0,
  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
};
const fileSizeStyle = {
  fontFamily: fontMono, fontSize: 10, letterSpacing: "0.08em",
  color: muted, textTransform: "uppercase", flexShrink: 0,
};

function DetailSection({ label, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        fontFamily: fontMono, fontSize: 10, letterSpacing: "0.12em",
        textTransform: "uppercase", color: muted, marginBottom: 8,
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <span style={{ width: 6, height: 6, background: accent, display: "inline-block" }}/>
        {label}
      </div>
      {children}
    </div>
  );
}

// ─── New project modal ──────────────────────────────────
function NewProjectModal({ employees, onCreate, onClose }) {
  const [title, setTitle] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [assignees, setAssignees] = useState([]);
  const [deadline, setDeadline] = useState("");
  const canSubmit = title.trim().length > 0;
  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 250,
        background: "rgba(10,10,15,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(640px, 96vw)", maxHeight: "92vh",
          background: paper, border: `2px solid ${ink}`,
          display: "flex", flexDirection: "column", overflow: "hidden",
          boxShadow: `8px 8px 0 ${accent}`,
        }}
      >
        <div style={{
          padding: "18px 22px", borderBottom: `2px solid ${ink}`,
          display: "flex", alignItems: "flex-start", gap: 14, justifyContent: "space-between",
        }}>
          <div style={{ flex: 1 }}>
            <div style={{
              fontFamily: fontMono, fontSize: 11, letterSpacing: "0.12em",
              textTransform: "uppercase", color: accent, fontWeight: 500, marginBottom: 8,
            }}>· NEW PROJECT · STARTS IN RESEARCH</div>
            <h2 style={{
              fontFamily: fontDisplay, fontSize: 28, textTransform: "uppercase",
              letterSpacing: "-0.02em", lineHeight: 0.95, margin: 0,
            }}>Dump research</h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent", border: `2px solid ${ink}`,
              width: 36, height: 36, cursor: "pointer", color: ink,
              display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}
          ><I.Close/></button>
        </div>
        <div style={{ padding: 22, overflowY: "auto" }}>
          <FieldRow label="Product *">
            <input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Magnetic bottle opener — wall mount"
              style={fieldInputStyle}
            />
          </FieldRow>
          <FieldRow label="Source URL">
            <input
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              placeholder="https://… reference link"
              style={fieldInputStyle}
            />
          </FieldRow>
          <FieldRow label="Notes" top>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Materials, process guess, target price, anything the designer needs to know…"
              style={{
                width: "100%", resize: "vertical", minHeight: 90,
                fontFamily: fontBody, fontSize: 13, lineHeight: 1.5,
                background: white, border: `2px solid ${ink}`,
                padding: "10px 12px", outline: "none", color: ink,
              }}
            />
          </FieldRow>
          <FieldRow label="Assign to" top>
            <AssigneePicker
              employees={employees}
              selected={assignees}
              onChange={setAssignees}
            />
          </FieldRow>
          <FieldRow label="Deadline">
            <input
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              style={fieldInputStyle}
            />
          </FieldRow>
        </div>
        <div style={{
          padding: "14px 22px", borderTop: `2px solid ${ink}`,
          display: "flex", gap: 10, justifyContent: "flex-end",
        }}>
          <button style={btnSm()} onClick={onClose}>CANCEL</button>
          <button
            style={{ ...btnSm("accent"), opacity: canSubmit ? 1 : 0.4, cursor: canSubmit ? "pointer" : "not-allowed" }}
            disabled={!canSubmit}
            onClick={() => onCreate({
              title: title.trim(),
              sourceUrl: sourceUrl.trim(),
              notes: notes.trim(),
              assignees,
              deadline: deadline || null,
            })}
          >
            CREATE PROJECT <I.Arrow s={12}/>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Alerts banner ──────────────────────────────────────
function AlertsBanner({ projects, onOpen }) {
  const alerts = projects.filter((p) => p.needsHelp);
  if (alerts.length === 0) return null;
  return (
    <div style={{
      background: dangerBg, borderBottom: `2px solid ${ink}`,
      padding: "12px 24px",
      display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
      fontFamily: fontMono, fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase",
    }}>
      <span style={{ color: danger, flexShrink: 0 }}><I.AlertFill s={20}/></span>
      <span style={{ background: danger, color: white, padding: "3px 8px", fontWeight: 700 }}>{alerts.length} NEEDS HELP</span>
      <div style={{ flex: 1, color: ink, fontWeight: 500, display: "flex", flexWrap: "wrap", gap: 14 }}>
        {alerts.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onOpen(p.id)}
            style={{
              color: accent, textDecoration: "underline", textUnderlineOffset: "3px",
              background: "transparent", border: "none", padding: 0,
              cursor: "pointer", font: "inherit",
              textTransform: "uppercase", letterSpacing: "0.06em",
            }}
          >
            {p.title}
            {p.needsHelp.helperName && (
              <span style={{ color: ink, marginLeft: 6 }}>→ {initials(p.needsHelp.helperName)}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Kanban view ────────────────────────────────────────
function KanbanView({ projects, priorityOf, onOpen, onStageChange, compact }) {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const byStage = useMemo(() => {
    const m = {};
    STAGES.forEach((s) => { m[s.id] = []; });
    projects.forEach((p) => { if (m[p.stage]) m[p.stage].push(p); });
    Object.keys(m).forEach((k) => m[k].sort((a, b) => priorityOf(a.id) - priorityOf(b.id)));
    return m;
  }, [projects, priorityOf]);

  return (
    <div style={{ flex: 1, overflowX: "auto", overflowY: "hidden", padding: "20px 24px 40px" }}>
      <div style={{
        display: "grid",
        gridAutoColumns: compact ? 240 : 280,
        gridAutoFlow: "column",
        gap: compact ? 10 : 14,
        minHeight: "calc(100vh - 280px)",
      }}>
        {STAGES.map((stage) => {
          const items = byStage[stage.id];
          const isTarget = overCol === stage.id;
          return (
            <div
              key={stage.id}
              onDragOver={(e) => { e.preventDefault(); setOverCol(stage.id); }}
              onDragLeave={() => setOverCol((prev) => (prev === stage.id ? null : prev))}
              onDrop={(e) => {
                e.preventDefault();
                if (dragId) onStageChange(dragId, stage.id);
                setDragId(null); setOverCol(null);
              }}
              style={{
                display: "flex", flexDirection: "column",
                background: isTarget ? "rgba(0,51,255,0.05)" : "rgba(10,10,15,0.02)",
                border: `1px solid ${isTarget ? accent : rule}`,
                minHeight: 200,
              }}
            >
              <div style={{
                padding: "12px 14px", borderBottom: `1px solid ${rule}`,
                display: "flex", alignItems: "center", gap: 8,
                background: paper, position: "sticky", top: 0,
              }}>
                <span style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: "0.12em", color: accent, fontWeight: 500 }}>{stage.num}</span>
                <span style={{
                  fontFamily: fontDisplay, fontSize: 14, textTransform: "uppercase",
                  letterSpacing: "-0.01em", lineHeight: 1, flex: 1,
                }}>{stage.name}</span>
                <span style={{
                  fontFamily: fontMono, fontSize: 11, letterSpacing: "0.08em",
                  color: muted, border: `1px solid ${rule}`, padding: "2px 6px",
                }}>{items.length}</span>
              </div>
              <div style={{ padding: compact ? 8 : 10, display: "flex", flexDirection: "column", gap: compact ? 6 : 10 }}>
                {items.map((p) => (
                  <ProjectCard
                    key={p.id}
                    project={p}
                    priorityRank={priorityOf(p.id)}
                    onOpen={onOpen}
                    compact={compact}
                    isDragging={dragId === p.id}
                    dragHandlers={{
                      onDragStart: (e) => {
                        setDragId(p.id);
                        e.dataTransfer.effectAllowed = "move";
                        try { e.dataTransfer.setData("text/plain", p.id); } catch (_) {}
                      },
                      onDragEnd: () => { setDragId(null); setOverCol(null); },
                    }}
                  />
                ))}
                {items.length === 0 && (
                  <div style={{
                    fontFamily: fontMono, color: muted, padding: "16px 10px",
                    border: `1px dashed ${rule}`, textAlign: "center", fontSize: 10,
                    letterSpacing: "0.12em",
                  }}>EMPTY</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── List view ──────────────────────────────────────────
function ListView({ projects, onOpen, onReorder, compact }) {
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  // "above" or "below" — which half of the over-row the cursor is in.
  // Splitting the drop zone in half doubles the target size and lets the
  // top row act as an "insert at top" hit area (top half).
  const [overPos, setOverPos] = useState(null);
  // Server returns projects already sorted by priority_rank ASC.
  const ordered = projects;
  const resetDrag = () => { setDragId(null); setOverId(null); setOverPos(null); };

  return (
    <div style={{ flex: 1, padding: "20px 24px 60px", maxWidth: 1680, margin: "0 auto", width: "100%" }}>
      <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontFamily: fontMono, fontSize: 12, color: accent, marginBottom: 4, letterSpacing: "0.12em", textTransform: "uppercase" }}>
            · PRIORITY QUEUE · DRAG TO REORDER
          </div>
          <div style={{ fontFamily: fontDisplay, fontSize: 24, textTransform: "uppercase", letterSpacing: "-0.02em", lineHeight: 1 }}>
            {ordered.length} active projects
          </div>
        </div>
        <div style={{ fontFamily: fontMono, color: muted, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}>
          P1 → P{ordered.length} · TOP IS HIGHEST PRIORITY
        </div>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", border: `2px solid ${ink}`, background: white }}>
        <thead>
          <tr>
            {["", "RANK", "", "PROJECT", "STAGE", "PROGRESS", "ASSIGNEE", "DEADLINE", "NOTES"].map((h, i) => (
              <th key={i} style={{
                fontFamily: fontMono, fontSize: 10, letterSpacing: "0.12em",
                textTransform: "uppercase", textAlign: i === 8 ? "right" : "left", fontWeight: 500,
                padding: "12px 14px",
                borderBottom: `2px solid ${ink}`, background: paper, color: ink,
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ordered.map((p, i) => (
            <ProjectRow
              key={p.id}
              project={p}
              priorityRank={i + 1}
              onOpen={onOpen}
              compact={compact}
              isDragging={dragId === p.id}
              dropPos={overId === p.id && dragId !== p.id ? overPos : null}
              gripHandlers={{
                onDragStart: (e) => {
                  setDragId(p.id);
                  e.dataTransfer.effectAllowed = "move";
                  try { e.dataTransfer.setData("text/plain", p.id); } catch (_) {}

                  // Show the whole row following the cursor: clone the <tr>
                  // inside a wrapper <table> (rows can't render standalone) and
                  // hand it to setDragImage. The browser snapshots it during
                  // this tick; we clean up on the next.
                  const tr = e.currentTarget.closest("tr");
                  if (tr) {
                    const rect = tr.getBoundingClientRect();
                    const clone = tr.cloneNode(true);
                    const wrap = document.createElement("table");
                    wrap.style.cssText = [
                      "position:absolute",
                      "top:-9999px",
                      "left:-9999px",
                      `width:${rect.width}px`,
                      "border-collapse:collapse",
                      "background:#FFFFFF",
                      "border:2px solid #0033FF",
                      "box-shadow:4px 4px 0 #0A0A0F",
                      "pointer-events:none",
                    ].join(";");
                    const tbody = document.createElement("tbody");
                    tbody.appendChild(clone);
                    wrap.appendChild(tbody);
                    document.body.appendChild(wrap);
                    e.dataTransfer.setDragImage(wrap, e.clientX - rect.left, e.clientY - rect.top);
                    setTimeout(() => wrap.remove(), 0);
                  }
                },
                onDragEnd: resetDrag,
              }}
              rowHandlers={{
                onDragOver: (e) => {
                  if (!dragId || dragId === p.id) return;
                  e.preventDefault();
                  const rect = e.currentTarget.getBoundingClientRect();
                  const pos = e.clientY < rect.top + rect.height / 2 ? "above" : "below";
                  if (overId !== p.id || overPos !== pos) {
                    setOverId(p.id);
                    setOverPos(pos);
                  }
                },
                onDragLeave: () => {
                  // Only clear if we're leaving THIS row's row (not jumping
                  // to another row, which will set overId immediately).
                  if (overId === p.id) {
                    // No-op: another row's onDragOver will overwrite. Skip
                    // a flicker by not clearing here.
                  }
                },
                onDrop: (e) => {
                  e.preventDefault();
                  if (dragId && dragId !== p.id) {
                    onReorder(dragId, p.id, overPos || "above");
                  }
                  resetDrag();
                },
              }}
            />
          ))}
          {ordered.length === 0 && (
            <tr>
              <td colSpan={9} style={{
                padding: 40, textAlign: "center",
                fontFamily: fontMono, color: muted, fontSize: 11,
                letterSpacing: "0.12em", textTransform: "uppercase",
              }}>NO PROJECTS MATCH</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Subbar (search + filters + view toggle) ────────────
// Two phase metrics to surface bottlenecks:
//   Design         = assigned (startedAt) → photography (photoAt)
//   Time to market = photography (photoAt) → listed (completedAt)
function phaseMetrics(project) {
  if (!project?.startedAt) return [];
  const start = new Date(project.startedAt);
  const photo = project.photoAt ? new Date(project.photoAt) : null;
  const done = project.completedAt ? new Date(project.completedAt) : null;
  // Floor at 1 day — any elapsed time (even a few hours) counts as a full day.
  const days = (a, b) => Math.max(1, Math.round((b - a) / 86400000));
  const out = [
    { label: "Design", sub: "assigned → photography", days: days(start, photo || new Date()), done: !!photo },
  ];
  if (photo) {
    out.push({ label: "Time to market", sub: "photography → listed", days: days(photo, done || new Date()), done: !!done });
  }
  return out;
}

// ─── Archive (CEO-only recovery of soft-deleted projects) ───
function ArchiveModal({ projects, onRestore, onPurge, onClose }) {
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 400, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 640, maxHeight: "82vh", background: paper, border: `2px solid ${ink}`, display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "16px 20px", borderBottom: `2px solid ${ink}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontFamily: fontDisplay, fontSize: 18, textTransform: "uppercase", letterSpacing: "-0.01em", color: ink }}>Archive</div>
            <div style={{ fontFamily: fontMono, fontSize: 11, color: muted, letterSpacing: "0.06em", textTransform: "uppercase", marginTop: 2 }}>Deleted projects · CEO recovery</div>
          </div>
          <button onClick={onClose} style={{ ...btnSm(), padding: "6px 10px" }}>CLOSE</button>
        </div>
        <div style={{ padding: 16, overflowY: "auto" }}>
          {projects.length === 0 ? (
            <div style={{ fontFamily: fontMono, fontSize: 12, color: muted, textTransform: "uppercase", letterSpacing: "0.08em", textAlign: "center", padding: 24 }}>Archive is empty</div>
          ) : projects.map((p) => (
            <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", border: `2px solid ${ink}`, marginBottom: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: fontMono, fontSize: 11, color: accent, letterSpacing: "0.06em" }}>{p.code}</div>
                <div style={{ fontWeight: 600, color: ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</div>
                <div style={{ fontFamily: fontMono, fontSize: 11, color: muted }}>
                  Deleted{p.deletedBy ? ` by ${p.deletedBy}` : ""}{p.deletedAt ? ` · ${new Date(p.deletedAt).toLocaleString()}` : ""}
                </div>
              </div>
              <button onClick={() => onRestore(p.id)} style={{ ...btnSm(), padding: "7px 12px" }}>RESTORE</button>
              <button onClick={() => onPurge(p.id)} style={{ fontFamily: fontMono, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", padding: "7px 12px", border: `1px solid ${danger}`, background: "transparent", color: danger, cursor: "pointer" }}>DELETE FOREVER</button>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

function SubBar({ view, setView, search, setSearch, alertFilter, setAlertFilter, assigneeFilter, setAssigneeFilter, alertCount, onNew, compact, setCompact, isCEO, onOpenArchive }) {
  return (
    <div style={{
      borderBottom: `1px solid ${rule}`, background: paper,
      padding: "14px 24px",
      display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
    }}>
      <button style={{ ...btnSm("accent"), padding: "9px 14px" }} onClick={onNew}>
        <I.Plus s={12}/> NEW PROJECT
      </button>
      {isCEO && (
        <button style={{ ...btnSm(), padding: "9px 14px" }} onClick={onOpenArchive} title="Archived projects (CEO only)">
          ARCHIVE
        </button>
      )}

      <input
        placeholder="SEARCH PROJECTS, CODES, ASSIGNEES…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{
          fontFamily: fontMono, fontSize: 12, letterSpacing: "0.04em",
          background: "transparent", border: `2px solid ${ink}`,
          padding: "9px 14px", minWidth: 280, color: ink, outline: "none",
        }}
      />

      <button
        onClick={() => setAlertFilter(!alertFilter)}
        style={{
          fontFamily: fontMono, fontSize: 11, letterSpacing: "0.08em",
          textTransform: "uppercase", padding: "7px 11px",
          border: `1px solid ${danger}`, cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: 6,
          background: alertFilter ? danger : "transparent",
          color: alertFilter ? white : danger,
        }}
      >
        <I.AlertFill s={12}/> NEEDS HELP · {alertCount}
      </button>

      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: muted }}>FILTER:</span>
        {[
          { v: "all", l: "ALL" },
          { v: "mine", l: "MINE" },
          { v: "unassigned", l: "UNASSIGNED" },
        ].map((opt) => (
          <button
            key={opt.v}
            onClick={() => setAssigneeFilter(opt.v)}
            style={{
              fontFamily: fontMono, fontSize: 11, letterSpacing: "0.08em",
              textTransform: "uppercase", padding: "7px 11px",
              border: `1px solid ${ink}`, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 6,
              background: assigneeFilter === opt.v ? ink : "transparent",
              color: assigneeFilter === opt.v ? white : ink,
            }}
          >{opt.l}</button>
        ))}
      </div>

      <button
        onClick={() => setCompact(!compact)}
        style={{
          fontFamily: fontMono, fontSize: 11, letterSpacing: "0.08em",
          textTransform: "uppercase", padding: "7px 11px",
          border: `1px solid ${ink}`, cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: 6,
          background: compact ? ink : "transparent",
          color: compact ? white : ink,
          marginLeft: "auto",
        }}
      >
        {compact ? "COMPACT" : "COMFORTABLE"}
      </button>

      <div style={{ display: "inline-flex", border: `2px solid ${ink}` }}>
        <button
          onClick={() => setView("kanban")}
          style={{
            padding: "9px 14px", fontFamily: fontBody, fontWeight: 700,
            fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase",
            background: view === "kanban" ? ink : "transparent",
            color: view === "kanban" ? white : ink, border: "none",
            display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
          }}
        ><I.Kanban/> KANBAN</button>
        <button
          onClick={() => setView("list")}
          style={{
            padding: "9px 14px", fontFamily: fontBody, fontWeight: 700,
            fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase",
            background: view === "list" ? ink : "transparent",
            color: view === "list" ? white : ink, border: "none",
            display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
            borderLeft: `2px solid ${ink}`,
          }}
        ><I.List/> PRIORITY QUEUE</button>
      </div>
    </div>
  );
}

// ─── Main page ──────────────────────────────────────────
export default function ProductDevelopmentPage({ employees = [] }) {
  // Server is the source of truth. Backend returns projects already sorted
  // by priority_rank ASC, so the array index IS the queue position.
  const [projects, setProjects] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [view, setView] = useState("kanban");
  const [compact, setCompact] = useState(false);
  const [search, setSearch] = useState("");
  const [alertFilter, setAlertFilter] = useState(false);
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [loaded, setLoaded] = useState(false);

  // Current user (from session) — used for "MINE" filter + comment authoring
  const sessionUser = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem("nycmfg-user") || "null"); }
    catch { return null; }
  }, []);
  const currentUser = sessionUser?.name || "You";
  // Live role from the employees prop (session can be stale after a perm change).
  const liveEmployee = sessionUser && employees.find((e) => String(e.empId) === String(sessionUser.empId));
  const isAdmin = (liveEmployee?.role || sessionUser?.role) === "admin";
  const isCEO = String(liveEmployee?.empId || sessionUser?.empId || "") === "01";
  const myEmpId = liveEmployee?.empId || sessionUser?.empId || "";
  const [showArchive, setShowArchive] = useState(false);
  const [archivedProjects, setArchivedProjects] = useState([]);

  const fetchProjects = useCallback(() => {
    return fetch(`${API_URL}/api/product-dev`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setProjects(data); })
      .catch(() => {});
  }, []);

  // CEO-only archive (soft-deleted projects) — recover or permanently purge.
  const fetchArchive = useCallback(() => {
    return fetch(`${API_URL}/api/product-dev/archive?empId=${encodeURIComponent(myEmpId)}`)
      .then((r) => r.json())
      .then((d) => { if (Array.isArray(d)) setArchivedProjects(d); })
      .catch(() => {});
  }, [myEmpId]);

  const openArchive = () => { setShowArchive(true); fetchArchive(); };

  const restoreProject = (id) => {
    fetch(`${API_URL}/api/product-dev/${id}/restore?empId=${encodeURIComponent(myEmpId)}`, { method: "POST" })
      .then((r) => r.json())
      .then(() => { fetchArchive(); fetchProjects(); })
      .catch(() => {});
  };

  const purgeProject = (id) => {
    // Passwords are hashed server-side and never stored client-side — the CEO
    // types it here and the backend verifies before purging.
    const password = window.prompt("Permanently delete this project? This CANNOT be undone.\n\nEnter your CEO password to confirm:");
    if (!password) return;
    fetch(`${API_URL}/api/product-dev/${id}/purge`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ empId: myEmpId, password }),
    })
      .then(async (r) => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || `Server ${r.status}`); })
      .then(() => fetchArchive())
      .catch((e) => window.alert(`Delete failed: ${e.message}`));
  };

  useEffect(() => {
    fetchProjects().finally(() => setLoaded(true));
  }, [fetchProjects]);

  // Priority rank is position in the ordered array, 1-indexed for display.
  const priorityOf = useCallback(
    (id) => projects.findIndex((p) => p.id === id) + 1,
    [projects]
  );

  const visible = useMemo(() => {
    let xs = projects;
    if (alertFilter) xs = xs.filter((p) => p.needsHelp);
    if (assigneeFilter === "mine") xs = xs.filter((p) => (p.assignees || []).includes(currentUser));
    if (assigneeFilter === "unassigned") xs = xs.filter((p) => !(p.assignees || []).length);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      xs = xs.filter((p) =>
        (p.title || "").toLowerCase().includes(q) ||
        (p.code || "").toLowerCase().includes(q) ||
        (p.notes || "").toLowerCase().includes(q) ||
        (p.assignees || []).some((n) => n.toLowerCase().includes(q))
      );
    }
    return xs;
  }, [projects, alertFilter, assigneeFilter, search, currentUser]);

  const alertCount = useMemo(() => projects.filter((p) => p.needsHelp).length, [projects]);

  // Optimistic local patch + PUT to server. Falls back to a refetch on error.
  const patchProject = (id, patch) => {
    setProjects((ps) => ps.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    fetch(`${API_URL}/api/product-dev/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    }).catch(() => { fetchProjects(); });
  };

  const onStageChange = (id, stage) => patchProject(id, { stage });

  // Drag-to-reorder: move the dragged row above or below the drop-target
  // row depending on which half of it the cursor was over. Bulk-updates
  // priority_rank on the server after the optimistic local move.
  const onReorder = (dragId, dropId, position = "above") => {
    if (dragId === dropId) return;
    setProjects((ps) => {
      const next = ps.filter((p) => p.id !== dragId);
      const dragged = ps.find((p) => p.id === dragId);
      if (!dragged) return ps;
      let dropIdx = next.findIndex((p) => p.id === dropId);
      if (dropIdx === -1) {
        next.push(dragged);
      } else {
        if (position === "below") dropIdx += 1;
        next.splice(dropIdx, 0, dragged);
      }
      // Persist the new order
      fetch(`${API_URL}/api/product-dev/reorder`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order: next.map((p) => p.id) }),
      }).catch(() => { fetchProjects(); });
      return next;
    });
  };

  const onCreate = (data) => {
    fetch(`${API_URL}/api/product-dev`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: data.title,
        sourceUrl: data.sourceUrl || "",
        notes: data.notes || "",
        stage: "research",
        assignees: Array.isArray(data.assignees) ? data.assignees : [],
        deadline: data.deadline || null,
      }),
    })
      .then((r) => r.json())
      .then((created) => {
        if (created && created.id) {
          setProjects((ps) => [created, ...ps.map((p) => ({ ...p, priorityRank: (p.priorityRank || 0) + 1 }))]);
          setShowNew(false);
          setOpenId(created.id);
        }
      })
      .catch(() => { fetchProjects(); setShowNew(false); });
  };

  const openProject = projects.find((p) => p.id === openId);
  const actions = openProject ? {
    priorityRank: priorityOf(openProject.id),
    refreshImage: () => {
      const u = openProject.sourceUrl;
      if (!u) return;
      fetch(`${API_URL}/api/link-preview?url=${encodeURIComponent(u)}`)
        .then((r) => r.json())
        .then((d) => { if (d && d.image) patchProject(openProject.id, { imageUrl: d.image }); })
        .catch(() => {});
    },
    setStage: (s) => patchProject(openProject.id, { stage: s }),
    setAssignees: (names) => patchProject(openProject.id, { assignees: Array.isArray(names) ? names : [] }),
    setDeadline: (d) => patchProject(openProject.id, { deadline: d }),
    flagHelp: ({ note, helperName }) => patchProject(openProject.id, { needsHelp: { note, helperName, at: nowStamp() } }),
    clearHelp: () => patchProject(openProject.id, { needsHelp: null }),
    addComment: (text) => patchProject(openProject.id, {
      comments: [...(openProject.comments || []), { author: currentUser, at: nowStamp(), text }],
    }),
    addLink: () => {
      const label = window.prompt("Link label (e.g. 'Drive — reference photos')");
      if (!label) return;
      const url = window.prompt("URL");
      if (!url) return;
      patchProject(openProject.id, {
        fileLinks: [...(openProject.fileLinks || []), { label, url }],
      });
    },
    uploadFile: (file) => {
      const fd = new FormData();
      const files = Array.isArray(file) ? file : [file];
      files.forEach((f) => fd.append("file", f));
      return fetch(`${API_URL}/api/product-dev/${openProject.id}/upload`, { method: "POST", body: fd })
        .then(async (r) => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || `Server returned ${r.status}`); return d; })
        .then((updated) => { setProjects((ps) => ps.map((p) => (p.id === updated.id ? updated : p))); })
        .catch((e) => window.alert(`Upload failed: ${e.message}`));
    },
    downloadAll: async () => {
      try {
        const r = await fetch(`${API_URL}/api/product-dev/${openProject.id}/download-all`);
        if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || `Server returned ${r.status}`); }
        const blob = await r.blob();
        const dispo = r.headers.get("Content-Disposition") || "";
        const m = dispo.match(/filename="?([^"]+)"?/);
        const name = m ? m[1] : `${(openProject.title || "project").replace(/[^A-Za-z0-9]/g, "")}.zip`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = name;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a); URL.revokeObjectURL(url);
      } catch (e) {
        window.alert(`Download failed: ${e.message}`);
      }
    },
    deleteProject: () => {
      const id = openProject.id;
      setProjects((ps) => ps.filter((p) => p.id !== id));
      setOpenId(null);
      fetch(`${API_URL}/api/product-dev/${id}?empId=${encodeURIComponent(myEmpId)}`, { method: "DELETE" })
        .then(async (r) => { if (!r.ok) throw new Error(); })
        .catch(() => { fetchProjects(); });
    },
    toggleTimer: (running) => {
      const id = openProject.id;
      const action = running ? "stop" : "start";
      return fetch(`${API_URL}/api/product-dev/${id}/timer/${action}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ empId: myEmpId }),
      })
        .then((r) => r.json())
        .then((u) => { if (u && u.id) setProjects((ps) => ps.map((p) => (p.id === u.id ? u : p))); })
        .catch(() => {});
    },
  } : null;

  // Empty state
  if (loaded && projects.length === 0) {
    return (
      <div style={{ background: paper, minHeight: "100%", display: "flex", flexDirection: "column" }}>
        <SubBar
          view={view} setView={setView}
          search={search} setSearch={setSearch}
          alertFilter={alertFilter} setAlertFilter={setAlertFilter}
          assigneeFilter={assigneeFilter} setAssigneeFilter={setAssigneeFilter}
          alertCount={alertCount}
          onNew={() => setShowNew(true)}
          compact={compact} setCompact={setCompact}
          isCEO={isCEO} onOpenArchive={openArchive}
        />
        <div style={{
          flex: 1, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", padding: "80px 24px", textAlign: "center",
        }}>
          <div style={{ fontFamily: fontMono, fontSize: 12, letterSpacing: "0.12em", color: accent, marginBottom: 12, textTransform: "uppercase" }}>
            · PRODUCT DEVELOPMENT · NO PROJECTS YET
          </div>
          <h2 style={{ fontFamily: fontDisplay, fontSize: 36, textTransform: "uppercase", letterSpacing: "-0.02em", margin: 0, maxWidth: 540 }}>
            Dump your first product research and assign it
          </h2>
          <p style={{ fontFamily: fontBody, fontSize: 15, color: muted, marginTop: 14, maxWidth: 540, lineHeight: 1.5 }}>
            Research → Assigned → Files Received → Prototyping → Needs Adjustment → Photography → Listed.
            Drag cards between stages, drag rows to reorder priority, flag projects that get stuck.
          </p>
          <button
            style={{ ...btnSm("accent"), marginTop: 22, padding: "12px 20px", fontSize: 13 }}
            onClick={() => setShowNew(true)}
          >
            <I.Plus s={14}/> NEW PROJECT
          </button>
        </div>
        {showNew && <NewProjectModal employees={employees} onCreate={onCreate} onClose={() => setShowNew(false)}/>}
      </div>
    );
  }

  return (
    <div style={{ background: paper, minHeight: "100%", display: "flex", flexDirection: "column" }}>
      <SubBar
        view={view} setView={setView}
        search={search} setSearch={setSearch}
        alertFilter={alertFilter} setAlertFilter={setAlertFilter}
        assigneeFilter={assigneeFilter} setAssigneeFilter={setAssigneeFilter}
        alertCount={alertCount}
        onNew={() => setShowNew(true)}
        compact={compact} setCompact={setCompact}
        isCEO={isCEO} onOpenArchive={openArchive}
      />
      <AlertsBanner projects={visible} onOpen={setOpenId}/>

      {view === "kanban" ? (
        <KanbanView
          projects={visible}
          priorityOf={priorityOf}
          onOpen={setOpenId}
          onStageChange={onStageChange}
          compact={compact}
        />
      ) : (
        <ListView
          projects={visible}
          onOpen={setOpenId}
          onReorder={onReorder}
          compact={compact}
        />
      )}

      {openProject && (
        <DetailDrawer
          project={openProject}
          employees={employees}
          actions={actions}
          canDelete={isAdmin}
          onClose={() => setOpenId(null)}
        />
      )}

      {showNew && (
        <NewProjectModal employees={employees} onCreate={onCreate} onClose={() => setShowNew(false)}/>
      )}
      {showArchive && (
        <ArchiveModal
          projects={archivedProjects}
          onRestore={restoreProject}
          onPurge={purgeProject}
          onClose={() => setShowArchive(false)}
        />
      )}
    </div>
  );
}
